import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { StatusBadge } from "@/components/Common";
import { fmtMoney } from "@/lib/format";
import {
  Calculator, TrendUp, Coins, Wallet, Target, Path, Truck, CalendarCheck,
  ChartBar, Receipt, ArrowUpRight, Printer, UserCircle,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, PieChart, Pie, Cell,
} from "recharts";

const PERIODS = [
  { k: "month", label: "This month" },
  { k: "ytd", label: "Year to date" },
  { k: "all", label: "All time" },
  { k: "custom", label: "Custom" },
];

const ymdOf = (t) => `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
const todayYmd = () => ymdOf(new Date());
const shift = (days) => { const t = new Date(); t.setDate(t.getDate() + days); return ymdOf(t); };
const QUICK = [
  { k: "today", label: "Today", range: () => [todayYmd(), todayYmd()] },
  { k: "week", label: "This week", range: () => { const dow = (new Date().getDay() + 6) % 7; return [shift(-dow), shift(6 - dow)]; } },
  { k: "last7", label: "Last 7 days", range: () => [shift(-6), todayYmd()] },
];

const STATUS_COLORS = {
  draft: "#94A3B8", accepted: "#0D9488", completed: "#059669",
  declined: "#EF4444", archived: "#CBD5E1",
};

const compact = (n) => {
  n = Number(n) || 0;
  if (Math.abs(n) >= 1e6) return `R ${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `R ${(n / 1e3).toFixed(0)}k`;
  return `R ${n.toFixed(0)}`;
};
const km = (n) => `${(Number(n) || 0).toLocaleString("en-ZA", { maximumFractionDigits: 0 })} km`;

export default function Dashboard() {
  const [period, setPeriod] = useState("ytd");
  const [byUser, setByUser] = useState("");
  const [from, setFrom] = useState(todayYmd());
  const [to, setTo] = useState(todayYmd());
  const [basis, setBasis] = useState("created");
  const applyQuick = (qk) => { const [a, b] = qk.range(); setFrom(a); setTo(b); setPeriod("custom"); };
  const quickActive = (qk) => { const [a, b] = qk.range(); return period === "custom" && from === a && to === b; };
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  const rangeInvalid = period === "custom" && (!from || !to || to < from);

  useEffect(() => {
    if (rangeInvalid) { return; }
    setD(null);
    const params = new URLSearchParams({ period, date_basis: basis });
    if (byUser) params.set("by_user", byUser);
    if (period === "custom") { params.set("date_from", from); params.set("date_to", to); }
    api.get(`/dashboard/executive?${params.toString()}`).then((r) => setD(r.data)).catch(() => {});
  }, [period, byUser, from, to, rangeInvalid, basis]);

  const k = d?.kpis || {};
  const statusData = Object.entries(d?.by_status || {})
    .filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }));
  const maxFleetRev = Math.max(...(d?.fleet_activity || []).flatMap((f) => [Number(f.revenue) || 0, Math.abs(Number(f.profit) || 0), Number(f.fuel_cost) || 0]), 1);
  const maxUserQuotes = Math.max(...(d?.user_activity || []).map((u) => Number(u.quotes) || 0), 1);

  return (
    <div className="pb-10">
      {/* Header + period filter */}
      <div className="flex flex-col gap-4 mb-6">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-teal-600 mb-1">Executive overview</div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-slate-900">Business dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Revenue, pipeline and fleet performance at a glance.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 no-print">
          <select value={byUser} onChange={(e) => setByUser(e.target.value)} data-testid="dash-user-filter" className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm bg-white max-w-[180px]">
            <option value="">All users</option>
            {(d?.quoters || []).map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
          <div className="inline-flex bg-slate-100 rounded-lg p-1" data-testid="date-basis-toggle" title="Which date the period filters on">
            {[["created", "Quote date"], ["trip", "Trip date"]].map(([b, l]) => (
              <button key={b} onClick={() => setBasis(b)} data-testid={`basis-${b}`}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${basis === b ? "bg-slate-900 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                {l}
              </button>
            ))}
          </div>
          <div className="inline-flex items-center gap-1" data-testid="quick-ranges">
            {QUICK.map((qk) => (
              <button key={qk.k} onClick={() => applyQuick(qk)} data-testid={`quick-${qk.k}`}
                className={`px-2.5 py-1.5 text-xs font-medium rounded-full border transition-colors ${quickActive(qk) ? "border-teal-600 bg-teal-50 text-teal-700" : "border-slate-300 text-slate-600 hover:border-slate-400"}`}>
                {qk.label}
              </button>
            ))}
          </div>
          <div className="inline-flex bg-slate-100 rounded-lg p-1" data-testid="period-filter">
            {PERIODS.map((p) => (
              <button key={p.k} onClick={() => setPeriod(p.k)} data-testid={`period-${p.k}`}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${period === p.k ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                {p.label}
              </button>
            ))}
          </div>
          {period === "custom" && (
            <div className="flex items-center gap-1.5" data-testid="custom-range">
              <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} data-testid="dash-date-from" className="px-2 py-1.5 border border-slate-300 rounded-lg text-sm bg-white" />
              <span className="text-slate-400 text-sm">to</span>
              <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} data-testid="dash-date-to" className="px-2 py-1.5 border border-slate-300 rounded-lg text-sm bg-white" />
              {rangeInvalid && <span className="text-xs text-red-500" data-testid="dash-range-error">Invalid range</span>}
            </div>
          )}
          <Button variant="outline" onClick={() => window.print()} data-testid="dash-export" title="Print / export snapshot"><Printer size={18} className="mr-2" />Export</Button>
          <Button data-testid="dash-new-quote" onClick={() => navigate("/quote")} className="bg-slate-900 hover:bg-slate-800 text-white">
            <Calculator size={18} weight="bold" className="mr-2" /> New Quote
          </Button>
        </div>
      </div>

      {/* Primary KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
        <Kpi testid="kpi-revenue" icon={Coins} accent="teal" label="Accepted revenue"
          value={fmtMoney(k.accepted_revenue)} sub={`${k.accepted_count ?? 0} accepted · excl VAT`} delta={k.deltas?.accepted_revenue} />
        <Kpi testid="kpi-profit" icon={TrendUp} accent="emerald" label="Gross profit"
          value={fmtMoney(k.gross_profit)} sub={`Avg margin ${k.avg_margin ?? 0}%`} delta={k.deltas?.gross_profit} />
        <Kpi testid="kpi-pipeline" icon={Wallet} accent="amber" label="Open pipeline"
          value={fmtMoney(k.pipeline_value)} sub={`${k.draft_count ?? 0} draft quotes`} delta={k.deltas?.pipeline_value} />
        <Kpi testid="kpi-winrate" icon={Target} accent="slate" label="Win rate"
          value={`${k.win_rate ?? 0}%`} sub={`Avg quote ${fmtMoney(k.avg_quote_value)}`} delta={k.deltas?.win_rate} />
      </div>

      {/* Secondary strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Mini testid="mini-km" icon={Path} label="Distance quoted" value={km(k.total_km)} />
        <Mini testid="mini-trips" icon={CalendarCheck} label="Active bookings" value={k.active_bookings ?? 0} />
        <Mini testid="mini-vehicles" icon={Truck} label="Active fleet" value={k.active_vehicles ?? 0} />
        <Mini testid="mini-quotes" icon={Receipt} label="Quotes in period" value={k.total_quotes ?? 0} />
      </div>

      {/* Own fleet vs 3rd-party hire */}
      {d?.hire_split && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-testid="hire-split">
          <HireCard title="Own fleet" icon={Truck} accent="teal" s={d.hire_split.own} />
          <HireCard title="3rd-party hire" icon={Receipt} accent="amber" s={d.hire_split.third_party} extra />
        </div>
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg" data-testid="trend-chart">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
            <ChartBar size={18} className="text-slate-400" weight="bold" />
            <h3 className="font-heading font-semibold text-slate-900">Quoted vs accepted · last 6 months</h3>
          </div>
          <div className="p-4" style={{ height: 300 }}>
            {d && (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={d.monthly} margin={{ top: 10, right: 8, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="l" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <YAxis yAxisId="r" orientation="right" tickFormatter={compact} tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                  <Tooltip formatter={(v, n) => (n === "Revenue" ? fmtMoney(v) : v)} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Bar yAxisId="l" dataKey="quoted" name="Quoted" fill="#CBD5E1" radius={[4, 4, 0, 0]} barSize={18} />
                  <Bar yAxisId="l" dataKey="accepted" name="Accepted" fill="#0D9488" radius={[4, 4, 0, 0]} barSize={18} />
                  <Line yAxisId="r" type="monotone" dataKey="revenue" name="Revenue" stroke="#0F172A" strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg" data-testid="status-chart">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="font-heading font-semibold text-slate-900">Quotes by status</h3>
          </div>
          <div className="p-4">
            <div style={{ height: 180 }}>
              {d && statusData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={72} paddingAngle={2}>
                      {statusData.map((s) => <Cell key={s.name} fill={STATUS_COLORS[s.name] || "#94A3B8"} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <div className="h-full flex items-center justify-center text-sm text-slate-400">No quotes in period</div>}
            </div>
            <div className="mt-3 space-y-2">
              {["draft", "accepted", "completed", "declined", "archived"].map((s) => (
                <div key={s} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLORS[s] }} /><StatusBadge status={s} /></span>
                  <span className="data-num font-semibold text-slate-900">{d?.by_status?.[s] ?? 0}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Clients + Fleet */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border border-slate-200 rounded-lg" data-testid="top-clients">
          <div className="px-6 py-4 border-b border-slate-100"><h3 className="font-heading font-semibold text-slate-900">Top clients by revenue</h3></div>
          <div className="divide-y divide-slate-100">
            {(d?.top_clients || []).map((c, i) => (
              <div key={c.name} className="flex items-center justify-between px-6 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-6 h-6 rounded-md bg-slate-100 text-slate-500 text-xs font-semibold flex items-center justify-center shrink-0">{i + 1}</span>
                  <div className="min-w-0"><div className="font-medium text-slate-900 text-sm truncate">{c.name}</div><div className="text-[11px] text-slate-400">{c.trips} trip{c.trips !== 1 ? "s" : ""}</div></div>
                </div>
                <span className="data-num text-sm font-semibold text-slate-900">{fmtMoney(c.revenue)}</span>
              </div>
            ))}
            {d && (d.top_clients || []).length === 0 && <div className="px-6 py-10 text-center text-slate-400 text-sm">No accepted quotes in period</div>}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg" data-testid="fleet-activity">
          <div className="px-6 py-4 border-b border-slate-100"><h3 className="font-heading font-semibold text-slate-900">Fleet activity</h3></div>
          <div className="p-4 space-y-3">
            {(d?.fleet_activity || []).map((f) => (
              <div key={f.name} data-testid={`fleet-${f.name}`} className="pb-2">
                <div className="flex items-center justify-between mb-1.5 text-sm">
                  <span className="flex items-center gap-2 font-medium text-slate-800"><Truck size={15} weight="fill" className="text-slate-400" />{f.name}</span>
                  <span className="text-[11px] text-slate-400">{f.trips} trip{f.trips !== 1 ? "s" : ""} · {km(f.km)}</span>
                </div>
                <FleetBar testid={`fleet-revenue-${f.name}`} label="Revenue" value={f.revenue} max={maxFleetRev} color="bg-teal-500" />
                <FleetBar testid={`fleet-fuel-${f.name}`} label="Fuel input" value={f.fuel_cost} max={maxFleetRev} color="bg-amber-500" na={f.is_third_party} />
                <FleetBar testid={`fleet-profit-${f.name}`} label="Profit" value={f.profit} max={maxFleetRev} color={Number(f.profit) < 0 ? "bg-red-500" : "bg-emerald-500"} suffix={`${f.margin_pct}%`} />
              </div>
            ))}
            {d && (d.fleet_activity || []).length === 0 && <div className="px-2 py-8 text-center text-slate-400 text-sm">No fleet activity in period</div>}
          </div>
        </div>
      </div>

      {/* Quotes by user */}
      <div className="bg-white border border-slate-200 rounded-lg mb-6" data-testid="user-activity">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
          <UserCircle size={18} className="text-slate-400" weight="bold" />
          <h3 className="font-heading font-semibold text-slate-900">Quotes by user</h3>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
          {(d?.user_activity || []).map((u) => (
            <div key={u.name} data-testid={`user-${u.name}`}>
              <div className="flex items-center justify-between mb-1 text-sm">
                <span className="flex items-center gap-2 text-slate-700"><span className="w-6 h-6 rounded-full bg-teal-50 text-teal-700 text-[11px] font-semibold flex items-center justify-center">{u.name.slice(0, 1).toUpperCase()}</span>{u.name}</span>
                <span className="data-num font-semibold text-slate-900">{u.quotes} quote{u.quotes !== 1 ? "s" : ""}</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-slate-800 rounded-full" style={{ width: `${(Number(u.quotes) / maxUserQuotes) * 100}%` }} />
              </div>
              <div className="text-[11px] text-slate-400 mt-0.5">{u.accepted} accepted · {fmtMoney(u.revenue)} revenue</div>
            </div>
          ))}
          {d && (d.user_activity || []).length === 0 && <div className="px-2 py-8 text-center text-slate-400 text-sm">No quotes in period</div>}
        </div>
      </div>

      {/* Recent quotes */}
      <div className="bg-white border border-slate-200 rounded-lg">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-heading font-semibold text-slate-900">Recent quotes</h3>
          <button className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1" onClick={() => navigate("/quotes")} data-testid="dash-view-all">View all <ArrowUpRight size={14} /></button>
        </div>
        <div className="divide-y divide-slate-100">
          {(d?.recent_quotes || []).map((q) => (
            <button key={q.id} onClick={() => navigate(`/quotes/${q.id}`)} className="w-full flex items-center justify-between px-6 py-3 hover:bg-slate-50 transition-colors text-left" data-testid={`recent-quote-${q.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2"><span className="font-mono text-sm font-semibold text-slate-900">{q.quote_number}</span><span className="text-xs text-slate-400">v{q.version}</span></div>
                <div className="text-sm text-slate-500 truncate">{q.client_snapshot?.name || q.title || "—"} · {q.vehicle_snapshot?.name || "—"}</div>
              </div>
              <div className="flex items-center gap-4 shrink-0"><span className="data-num text-sm text-slate-700">{fmtMoney(q.costing?.selling_ex_vat)}</span><StatusBadge status={q.status} /></div>
            </button>
          ))}
          {d && (d.recent_quotes || []).length === 0 && <div className="px-6 py-10 text-center text-slate-400 text-sm">No quotes in this period.</div>}
        </div>
      </div>
    </div>
  );
}

const ACCENTS = {
  teal: "bg-teal-50 text-teal-600", emerald: "bg-emerald-50 text-emerald-600",
  amber: "bg-amber-50 text-amber-600", slate: "bg-slate-100 text-slate-600",
};
function Kpi({ icon: Icon, label, value, sub, accent, testid, delta }) {
  const hasDelta = typeof delta === "number";
  const up = hasDelta && delta >= 0;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
        <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${ACCENTS[accent]}`}><Icon size={18} weight="bold" /></span>
      </div>
      <div className="flex items-baseline gap-2">
        <div className="font-heading font-bold text-2xl text-slate-900 data-num truncate">{value}</div>
        {hasDelta && <span className={`text-[11px] font-semibold shrink-0 ${up ? "text-emerald-600" : "text-red-500"}`}>{up ? "▲" : "▼"} {Math.abs(delta)}%</span>}
      </div>
      <div className="text-xs text-slate-400 mt-1">{sub}{hasDelta ? " · vs prev" : ""}</div>
    </div>
  );
}
function Mini({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 flex items-center gap-3" data-testid={testid}>
      <span className="w-9 h-9 rounded-md bg-slate-100 flex items-center justify-center shrink-0"><Icon size={18} className="text-slate-500" weight="bold" /></span>
      <div className="min-w-0"><div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div><div className="data-num font-semibold text-slate-900 truncate">{value}</div></div>
    </div>
  );
}
function HireCard({ title, icon: Icon, accent, s, extra }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid={`hire-card-${extra ? "third" : "own"}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</span>
        <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${ACCENTS[accent]}`}><Icon size={18} weight="bold" /></span>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
        <HireStat label="Accepted trips" value={s?.count ?? 0} />
        <HireStat label="Revenue (excl VAT)" value={fmtMoney(s?.revenue)} />
        <HireStat label="Gross profit" value={fmtMoney(s?.gross_profit)} />
        <HireStat label="Avg margin" value={`${s?.avg_margin ?? 0}%`} />
        {extra && <HireStat label="Supplier hire cost" value={fmtMoney(s?.hire_cost)} />}
      </div>
    </div>
  );
}
function FleetBar({ label, value, max, color, na, suffix, testid }) {
  const v = Math.abs(Number(value) || 0);
  return (
    <div className="grid grid-cols-[72px_1fr_110px_52px] items-center gap-3 mt-1" data-testid={testid}>
      <span className="text-[11px] uppercase tracking-wider text-slate-400">{label}</span>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        {!na && <div className={`h-full ${color} rounded-full`} style={{ width: `${(v / max) * 100}%` }} />}
      </div>
      <span className="data-num text-xs font-semibold text-slate-900 text-right">{na ? "—" : fmtMoney(value)}</span>
      <span className="data-num text-[11px] text-slate-400 text-right">{suffix || ""}</span>
    </div>
  );
}
function HireStat({ label, value }) {
  return <div><div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div><div className="data-num font-semibold text-slate-900 text-sm truncate">{value}</div></div>;
}
